import { z } from 'zod';
import type { TFunction } from 'i18next';

// Допустимые спецсимволы — печатная ASCII-пунктуация. Единственный источник
// правды: из этой строки собираются и регулярки, и список в тексте ошибки, так
// что перечисление в сообщении не может разойтись с тем, что реально
// проверяется. Менеджеру, которого развернули на `~`, прежнее «пароль должен
// содержать латинские буквы, цифры и специальные символы» ничего не объясняло:
// всё перечисленное в пароле было.
export const PASSWORD_SPECIAL_CHARS = '!@#$%^&*()_+-=[]{};\':"\\|,.<>/?';

// Внутри [...] особый смысл имеют только эти четыре символа.
const escapeForCharClass = (chars: string) => chars.replace(/[\\\]^-]/g, '\\$&');

const SPECIAL_PATTERN = new RegExp(`[${escapeForCharClass(PASSWORD_SPECIAL_CHARS)}]`);
const ALLOWED_CHAR_PATTERN = new RegExp(
  `^[a-zA-Z0-9${escapeForCharClass(PASSWORD_SPECIAL_CHARS)}]$`
);

/**
 * Уникальные символы пароля, не прошедшие белый список, в порядке появления.
 * Обход через for..of — по кодпойнтам, иначе эмодзи развалился бы на суррогаты
 * и в сообщении оказались бы два битых символа вместо одного целого.
 */
export const findDisallowedChars = (value: string): string[] => {
  const disallowed = new Set<string>();

  for (const char of value) {
    if (!ALLOWED_CHAR_PATTERN.test(char)) {
      disallowed.add(char);
    }
  }

  return [...disallowed];
};

// Пробел, неразрывный пробел и управляющие символы в сообщении не видны —
// показываем их кодом, иначе «Недопустимые символы: » обрывается пустотой.
const formatChar = (char: string): string => {
  const code = char.codePointAt(0) ?? 0;

  return /\s/.test(char) || code < 0x20 || code === 0x7f
    ? `U+${code.toString(16).toUpperCase().padStart(4, '0')}`
    : char;
};

// Сколько символов перечислять. Пароль, набранный целиком кириллицей, дал бы
// список на весь алфавит — обрезаем, исправив показанные, партнёр увидит
// следующую порцию.
const MAX_LISTED_CHARS = 8;

/**
 * Перечисление недопустимых символов для подстановки в сообщение.
 * Разделитель — запятая: сама она в белом списке, среди недопустимых
 * оказаться не может, так что перечисление читается однозначно.
 */
export const formatDisallowedChars = (value: string): string => {
  const disallowed = findDisallowedChars(value);
  const listed = disallowed.slice(0, MAX_LISTED_CHARS).map(formatChar).join(', ');

  return disallowed.length > MAX_LISTED_CHARS ? `${listed} …` : listed;
};

// Требования к паролю — целиком клиентские: на бэке (common/models/RegisterForm)
// у пароля только `string, min 6`. Фабрика, а не готовая схема: сообщения
// приходят из словаря (namespace `auth`), а схема, собранная при импорте модуля,
// застыла бы на языке первого рендера.
export const createPasswordSchema = (t: TFunction<'auth'>) =>
  z
    .string()
    .min(8, { message: t('password.minLength') })
    .refine((val) => /[a-zA-Z]/.test(val), { message: t('password.letter') })
    .refine((val) => /[0-9]/.test(val), { message: t('password.digit') })
    .refine((val) => SPECIAL_PATTERN.test(val), {
      message: t('password.special', { allowed: PASSWORD_SPECIAL_CHARS }),
    })
    // superRefine, а не refine: сообщение зависит от самого значения — нужно
    // назвать те символы, из-за которых пароль развернули.
    .superRefine((val, ctx) => {
      const disallowed = formatDisallowedChars(val);

      if (!disallowed) return;

      ctx.addIssue({
        code: 'custom',
        message: t('password.charset', {
          chars: disallowed,
          allowed: PASSWORD_SPECIAL_CHARS,
        }),
      });
    });
